import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate, Link } from "react-router-dom";
import { Input } from "@/shared/components/Input";
import { Button } from "@/shared/components/Button";
import { AuthCardLayout } from "@/shared/components/AuthCardLayout";
import { authService } from "@/core/services/authService";

const schema = z
  .object({
    novaSenha: z.string().min(6, "A senha precisa ter pelo menos 6 caracteres."),
    confirmarSenha: z.string().min(1, "Confirme a nova senha."),
  })
  .refine((dados) => dados.novaSenha === dados.confirmarSenha, {
    message: "As senhas não coincidem.",
    path: ["confirmarSenha"],
  });

type FormData = z.infer<typeof schema>;

/**
 * Tela acessada pelo link enviado no e-mail de recuperação de senha (fluxo
 * nativo do Supabase Auth). O Supabase já estabelece uma sessão temporária
 * de recuperação automaticamente ao carregar esta página (detectSessionInUrl
 * é padrão no client) — não é necessário nenhum código extra para isso.
 */
export function RedefinirSenha() {
  const navigate = useNavigate();
  const [erro, setErro] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  async function onSubmit(dados: FormData) {
    setErro(null);
    try {
      await authService.definirNovaSenha(dados.novaSenha);
      setSucesso(true);
      await authService.logout();
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível redefinir a senha.");
    }
  }

  return (
    <AuthCardLayout
      titulo="Definir nova senha"
      rodape={
        <Link to="/login" className="font-bold text-primary hover:underline">
          Voltar para o login
        </Link>
      }
    >
      {sucesso ? (
        <p className="text-center text-sm leading-relaxed text-text">
          Senha redefinida com sucesso! Redirecionando para o login...
        </p>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
          <Input
            label="Nova senha"
            type="password"
            autoComplete="new-password"
            erro={errors.novaSenha?.message}
            {...register("novaSenha")}
          />
          <Input
            label="Confirmar nova senha"
            type="password"
            autoComplete="new-password"
            erro={errors.confirmarSenha?.message}
            {...register("confirmarSenha")}
          />

          {erro && (
            <p role="alert" className="rounded-md bg-danger/10 px-3 py-2 text-xs text-danger">
              {erro}
            </p>
          )}

          <Button type="submit" carregando={isSubmitting} className="mt-1">
            Redefinir senha
          </Button>
        </form>
      )}
    </AuthCardLayout>
  );
}
